import React, { useState, useCallback, useEffect, useRef } from "react";
import { getCurrentUser, signOut } from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import { LanguageProvider, useLanguage } from "./utils/i18n";
import Header from "./components/Header";
import PdfPanel from "./components/PdfPanel";
import AiPanel from "./components/AiPanel";
import AuthPage from "./components/AuthPage";
import HomePage from "./components/HomePage";
import LiveTranslation from "./components/LiveTranslation";
import PaywallPage from "./components/PaywallPage";
import AdminBillingPage from "./components/AdminBillingPage";
import ProfileModal from "./components/ProfileModal";
import BookCreatorModal from "./components/BookCreatorModal";
import BookEditorPage from "./components/BookEditorPage";
import GeneralChatPage from "./components/GeneralChatPage";
import { getAuthHeaders } from "./utils/auth";

const API = "/api";

export default function App() {
  const { t } = useLanguage();
  const [user, setUser]               = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [books, setBooks]             = useState([]);
  const [activeBook, setActiveBook]   = useState(null);
  const [toast, setToast]             = useState(null);
  const [panelWidth, setPanelWidth]   = useState(() => Math.floor((window.innerWidth - 6) / 2));
  const [notes, setNotes]             = useState([]);
  const [page, setPage]               = useState("home"); // home | reader | chat | live-translate | billing | admin-billing | book-editor

  // ── HTML Books state ────────────────────────────────────────────────
  const [htmlBooks, setHtmlBooks]         = useState([]);
  const [activeHtmlBookId, setActiveHtmlBookId] = useState(null);
  const [createBookOpen, setCreateBookOpen] = useState(false);
  const [isMobile, setIsMobile]       = useState(() => window.innerWidth <= 768);
  const [aiSheetOpen, setAiSheetOpen] = useState(false);
  const [pdfHidden, setPdfHidden]     = useState(false);

  // ── Profile state ───────────────────────────────────────────────────
  const [profile, setProfile]         = useState({ name: "", school: "", major: "", email: "", permission_to_save_docs: "N" });
  const [profileOpen, setProfileOpen] = useState(false);

  // Refs for session-only upload tracking and S3 cleanup
  const tempBookIdRef  = useRef(null); // book_id of the current session-only upload
  const cachedTokenRef = useRef(null); // latest auth token (for keepalive fetch on page close)

  // Upload progress: null = idle, 0-100 = uploading
  const [uploadProgress, setUploadProgress] = useState(null);
  const [billingStatus, setBillingStatus] = useState(null);
  const [billingChecked, setBillingChecked] = useState(false);

  // ── Mobile breakpoint ───────────────────────────────────────────────
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ── Auth state ─────────────────────────────────────────────────────
  useEffect(() => {
    getCurrentUser()
      .then(setUser)
      .catch(() => setUser(null))
      .finally(() => setAuthChecked(true));

    const unsub = Hub.listen("auth", ({ payload }) => {
      if (payload.event === "signedIn")  getCurrentUser().then(setUser);
      if (payload.event === "signedOut") {
        // Clean up any session-only S3 upload before credentials are invalidated
        const bookToDelete = tempBookIdRef.current;
        const tokenToUse   = cachedTokenRef.current;
        tempBookIdRef.current  = null;
        cachedTokenRef.current = null;
        if (bookToDelete && tokenToUse) {
          fetch(`${API}/books/${bookToDelete}/temp`, {
            method: "DELETE",
            headers: { Authorization: tokenToUse },
          }).catch(() => {});
        }
        setUser(null); setBooks([]); setNotes([]); setActiveBook(null);
        setBillingStatus(null); setBillingChecked(false);
        setHtmlBooks([]); setActiveHtmlBookId(null);
        setPage("home");
      }
    });
    return unsub;
  }, []);

  // Load billing status once authenticated
  const fetchBillingStatus = useCallback(async () => {
    try {
      const authHeaders = await getAuthHeaders();
      const res = await fetch(`${API}/billing/status`, { headers: authHeaders });
      if (res.ok) {
        const data = await res.json();
        setBillingStatus(data);
      }
    } catch {}
    finally { setBillingChecked(true); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load profile data once authenticated
  const fetchProfile = useCallback(async () => {
    try {
      const authHeaders = await getAuthHeaders();
      const res = await fetch(`${API}/profile`, { headers: authHeaders });
      if (res.ok) {
        const data = await res.json();
        setProfile(data);
      }
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user) return;
    fetchBillingStatus();
    fetchProfile();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Handle Stripe redirect back (success / cancel)
  useEffect(() => {
    const path = window.location.pathname;
    if (path === "/billing/success") {
      fetchBillingStatus();
      window.history.replaceState({}, "", "/");
      setPage("billing");
    } else if (path === "/billing/cancel") {
      window.history.replaceState({}, "", "/");
      setPage("billing");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper: fetch with Authorization header
  const authedFetch = useCallback(async (url, options = {}) => {
    const authHeaders = await getAuthHeaders();
    if (authHeaders.Authorization) cachedTokenRef.current = authHeaders.Authorization;
    return fetch(url, {
      ...options,
      headers: { ...(options.headers || {}), ...authHeaders },
    });
  }, []);

  const showToast = useCallback((msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }, []);

  // Delete a saved document (DynamoDB + S3)
  const deleteBook = useCallback(async (bookId) => {
    if (!bookId) return;
    if (!window.confirm(t("deleteDocTitle"))) return;
    try {
      await authedFetch(`${API}/books/${bookId}`, { method: "DELETE" });
      setBooks((prev) => prev.filter((b) => b.id !== bookId));
      if (activeBook?.id === bookId) setActiveBook(null);
      showToast("Document deleted.");
    } catch {
      showToast("Delete failed — please try again.");
    }
  }, [authedFetch, activeBook, showToast, t]);

  // Delete a session-only upload from S3 (no DynamoDB entry was created for it)
  const deleteTempBook = useCallback(async (bookId) => {
    if (!bookId) return;
    try { await authedFetch(`${API}/books/${bookId}/temp`, { method: "DELETE" }); } catch {}
    if (tempBookIdRef.current === bookId) tempBookIdRef.current = null;
  }, [authedFetch]);

  // Called when user clicks Reupload while a book is open — confirms, cleans up S3, clears book
  const handleReupload = useCallback(async () => {
    if (tempBookIdRef.current) await deleteTempBook(tempBookIdRef.current);
    setActiveBook(null);
    return true;
  }, [deleteTempBook]);

  // Show a leave-site confirmation and clean up the S3 file via keepalive fetch
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (!tempBookIdRef.current) return;
      e.preventDefault();
      e.returnValue = ""; // triggers browser's "Leave site?" dialog
    };
    const handlePageHide = () => {
      if (!tempBookIdRef.current || !cachedTokenRef.current) return;
      // keepalive lets the DELETE complete even as the page unloads
      fetch(`${API}/books/${tempBookIdRef.current}/temp`, {
        method: "DELETE",
        headers: { Authorization: cachedTokenRef.current },
        keepalive: true,
      }).catch(() => {});
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("pagehide", handlePageHide);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, []); // stable: uses only refs
  useEffect(() => {
    if (!user) return;
    authedFetch(`${API}/notes`)
      .then((r) => r.json())
      .then((data) => { if (Array.isArray(data)) setNotes(data); })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // ── Subfolders + folder order (persisted in backend) ──────────────
  const [subfolders, setSubfolders] = useState([]);
  const [folderOrder, setFolderOrder] = useState([]);

  // Save folders to backend helper
  const saveFolders = useCallback(async (newSubfolders, newFolderOrder) => {
    try {
      await authedFetch(`${API}/folders`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ subfolders: newSubfolders, folderOrder: newFolderOrder }),
      });
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authedFetch]);

  // Load folders from backend once authenticated.
  // Always use DynamoDB as the single source of truth — clear any stale localStorage.
  useEffect(() => {
    if (!user) return;
    // Wipe legacy localStorage keys so they can never override DynamoDB again
    localStorage.removeItem("notes-subfolders");
    localStorage.removeItem("notes-folder-order");
    authedFetch(`${API}/folders`)
      .then((r) => r.json())
      .then((data) => {
        setSubfolders(Array.isArray(data.subfolders) ? data.subfolders : []);
        setFolderOrder(Array.isArray(data.folderOrder) ? data.folderOrder.filter(id => id !== "general") : []);
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const addNote = useCallback(async (title, content, folder = undefined) => {
    // If no folders exist yet, auto-create a default one first
    let effectiveSubfolders = subfolders;
    let effectiveFolderOrder = folderOrder;

    if ((!subfolders || subfolders.length === 0) && folder === undefined) {
      const lang = localStorage.getItem("notemila-lang") || "zh";
      const defaultFolder = { id: Date.now(), name: lang === "zh" ? "默认" : "Default" };
      effectiveSubfolders = [defaultFolder];
      effectiveFolderOrder = [defaultFolder.id];
      setSubfolders(effectiveSubfolders);
      setFolderOrder(effectiveFolderOrder);
      await saveFolders(effectiveSubfolders, effectiveFolderOrder);
    }

    // Default to first item in folderOrder
    const targetFolder = folder !== undefined
      ? folder
      : (() => {
          if (effectiveFolderOrder && effectiveFolderOrder.length > 0) {
            return effectiveFolderOrder[0] || null;
          }
          return effectiveSubfolders.find((f) => !f.parent)?.id || null;
        })();
    const note = {
      id: Date.now(),
      title,
      content,
      folder: targetFolder,
      timestamp: new Date().toLocaleString(),
      sortTs: Date.now(),
    };
    try {
      const res = await authedFetch(`${API}/notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(note),
      });
      const saved = await res.json();
      setNotes((prev) => [saved, ...prev]);
    } catch {
      // Fallback: add locally if backend unreachable
      setNotes((prev) => [note, ...prev]);
    }
    showToast("Added to notes! 📌");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showToast, subfolders, folderOrder, authedFetch, saveFolders]);

  const deleteNote = useCallback(async (id) => {
    setNotes((prev) => prev.filter((n) => n.id !== id));
    try {
      await authedFetch(`${API}/notes/${id}`, { method: "DELETE" });
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authedFetch]);

  const renameNote = useCallback(async (id, newTitle) => {
    setNotes((prev) => prev.map((n) => n.id === id ? { ...n, title: newTitle } : n));
    try {
      await authedFetch(`${API}/notes/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: newTitle }),
      });
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authedFetch]);

  const updateNoteContent = useCallback(async (id, newContent) => {
    setNotes((prev) => prev.map((n) => n.id === id ? { ...n, content: newContent } : n));
    try {
      await authedFetch(`${API}/notes/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content: newContent }),
      });
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authedFetch]);

  const addSubfolder = useCallback((name) => {
    const f = { id: Date.now(), name };
    setSubfolders((prevSubs) => {
      const nextSubs = [...prevSubs, f];
      setFolderOrder((prevOrd) => {
        const nextOrd = [f.id, ...(prevOrd || []).filter(id => id !== "general")];
        saveFolders(nextSubs, nextOrd);
        return nextOrd;
      });
      return nextSubs;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveFolders]);

  const deleteSubfolder = useCallback((folderId) => {
    // Collect all descendant folder IDs (the folder + any children recursively)
    setSubfolders((prevSubs) => {
      const allIds = new Set();
      const collect = (id) => {
        allIds.add(String(id));
        prevSubs.filter(f => String(f.parent) === String(id)).forEach(c => collect(c.id));
      };
      collect(folderId);

      // Delete all notes that belong to any of these folders
      setNotes((prev) => {
        const toDelete = prev.filter(n => allIds.has(String(n.folder)));
        toDelete.forEach(n => {
          authedFetch(`${API}/notes/${n.id}`, { method: "DELETE" }).catch(() => {});
        });
        return prev.filter(n => !allIds.has(String(n.folder)));
      });

      const nextSubs = prevSubs.filter(f => !allIds.has(String(f.id)));
      setFolderOrder((prevOrd) => {
        const nextOrd = (prevOrd || []).filter(id => !allIds.has(String(id)));
        saveFolders(nextSubs, nextOrd);
        return nextOrd;
      });
      return nextSubs;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveFolders, authedFetch]);

  const moveFolderToParent = useCallback((folderId, newParentId) => {
    setSubfolders((prevSubs) => {
      if (folderId === newParentId) return prevSubs;
      // Prevent circular: newParentId must not be a descendant of folderId
      const isDesc = (id, ancestorId) => {
        let cur = prevSubs.find((f) => f.id === id);
        while (cur && cur.parent != null) {
          if (cur.parent === ancestorId) return true;
          cur = prevSubs.find((f) => f.id === cur.parent);
        }
        return false;
      };
      if (newParentId !== null && isDesc(newParentId, folderId)) return prevSubs;
      const nextSubs = prevSubs.map((f) => f.id === folderId ? { ...f, parent: newParentId } : f);
      setFolderOrder((prevOrd) => { saveFolders(nextSubs, prevOrd); return prevOrd; });
      return nextSubs;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveFolders]);

  const reorderFolderOrder = useCallback((sourceId, targetId, position) => {
    setFolderOrder((prevOrd) => {
      if (!prevOrd) return prevOrd;
      const next = [...prevOrd];
      const fromIdx = next.findIndex(id => String(id) === String(sourceId));
      if (fromIdx === -1) return prevOrd;
      const [moved] = next.splice(fromIdx, 1);
      let toIdx = next.findIndex(id => String(id) === String(targetId));
      if (toIdx === -1) { next.push(moved); }
      else {
        if (position === "after") toIdx++;
        next.splice(toIdx, 0, moved);
      }
      setSubfolders((prevSubs) => { saveFolders(prevSubs, next); return prevSubs; });
      return next;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveFolders]);

  const moveNoteToFolder = useCallback(async (noteId, folderId) => {
    setNotes((prev) => prev.map((n) => n.id === noteId ? { ...n, folder: folderId } : n));
    try {
      await authedFetch(`${API}/notes/${noteId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ folder: folderId }),
      });
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authedFetch]);

  // Load previously uploaded books once authenticated
  useEffect(() => {
    if (!user) return;
    authedFetch(`${API}/books`)
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          const loaded = data.map((b) => ({ ...b, pdfUrl: `${API}/books/${b.id}/pdf` }));
          setBooks(loaded);
          setActiveBook(loaded[0]);
        }
      })
      .catch(() => {});
    // Load HTML books
    authedFetch(`${API}/html-books`)
      .then((r) => r.json())
      .then((data) => { if (Array.isArray(data)) setHtmlBooks(data); })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleUpload = (file) => {
    // Clean up previous session-only upload from S3 before replacing it
    if (tempBookIdRef.current) deleteTempBook(tempBookIdRef.current);

    setUploadProgress(0);
    getAuthHeaders().then((authHeaders) => {
      if (authHeaders.Authorization) cachedTokenRef.current = authHeaders.Authorization;

      const form = new FormData();
      form.append("file", file);

      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${API}/upload`);
      if (authHeaders.Authorization) xhr.setRequestHeader("Authorization", authHeaders.Authorization);

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          // Cap at 99 — the remaining 1% represents server-side processing
          setUploadProgress(Math.min(99, Math.round((e.loaded / e.total) * 100)));
        }
      };

      // Bytes fully sent; server is now processing (text extraction, S3 write, etc.)
      xhr.upload.onload = () => {
        setUploadProgress("processing");
      };

      xhr.onload = () => {
        setUploadProgress(null);
        try {
          const data = JSON.parse(xhr.responseText);
          if (xhr.status >= 200 && xhr.status < 300) {
            const newBook = { ...data, pdfUrl: `${API}/books/${data.id}/pdf` };
            setBooks((prev) => [...prev, newBook]);
            setActiveBook(newBook);
            showToast(`"${data.filename}" uploaded successfully!`);
            if (profile.permission_to_save_docs !== "Y") {
              tempBookIdRef.current = data.id;
            }
          } else {
            showToast(data.error || "Upload failed");
          }
        } catch {
          showToast("Upload failed.");
        }
      };

      xhr.onerror = () => {
        setUploadProgress(null);
        showToast("Upload failed. Is the backend running?");
      };

      xhr.send(form);
    });
  };

  const handleSelectBook = (bookId) => {
    const book = books.find((b) => b.id === bookId);
    if (book) setActiveBook(book);
  };

  /* ── Resizable divider ──────────────── */
  const handleMouseDown = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = panelWidth;

    const onMouseMove = (e) => {
      const diff = startX - e.clientX;
      setPanelWidth(Math.max(340, Math.min(800, startWidth + diff)));
    };
    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  // Show loading spinner while checking auth
  if (!authChecked) {
    return (
      <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100vh", background:"#1a1f35", color:"#fff", fontSize:18 }}>
        Loading…
      </div>
    );
  }

  // Show login/register page if not authenticated
  if (!user) {
    return <AuthPage onSignIn={() => { setPage("home"); getCurrentUser().then(setUser); }} />;
  }

  // Wait for billing check before rendering app
  if (!billingChecked) {
    return (
      <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100vh", background:"#1a1f35", color:"#fff", fontSize:18 }}>
        Loading…
      </div>
    );
  }

  // ── Admin billing page ───────────────────────────────────────────────────
  if (page === "admin-billing") {
    return <AdminBillingPage onBack={() => setPage("home")} />;
  }

  // ── Billing / paywall page ───────────────────────────────────────────────
  if (page === "billing" || billingStatus?.plan === "expired") {
    return (
      <PaywallPage
        billingStatus={billingStatus}
        onBack={billingStatus?.plan !== "expired" ? () => setPage("home") : null}
        onRefresh={async () => { await fetchBillingStatus(); if (billingStatus?.plan !== "expired") setPage("home"); }}
      />
    );
  }

  // ── General Chat page ──────────────────────────────────────
  if (page === "chat") {
    return (
      <>
        <GeneralChatPage
          onBack={() => setPage("home")}
          api={API}
          notes={notes}
          onAddNote={addNote}
        />
        {toast && <div className="toast">{toast}</div>}
      </>
    );
  }

  // ── Live Translation page ──────────────────────────────────
  if (page === "live-translate") {
    return <LiveTranslation onBack={() => setPage("home")} />;
  }

  // ── Book editor page ───────────────────────────────────────
  if (page === "book-editor" && activeHtmlBookId) {
    return (
      <>
        <BookEditorPage
          bookId={activeHtmlBookId}
          onBack={() => setPage("home")}
          showToast={showToast}
          isMobile={isMobile}
          notes={notes}
          onAddNote={addNote}
          onDeleteNote={deleteNote}
          onRenameNote={renameNote}
          onUpdateNote={updateNoteContent}
          subfolders={subfolders}
          onAddSubfolder={addSubfolder}
          onDeleteSubfolder={deleteSubfolder}
          onMoveNote={moveNoteToFolder}
          onMoveFolderToParent={moveFolderToParent}
          folderOrder={folderOrder}
          onReorderFolderOrder={reorderFolderOrder}
        />
        {toast && <div className="toast">{toast}</div>}
      </>
    );
  }

  // ── Home page ────────────────────────────────────────────────────────────
  if (page === "home") {
    const userName = (profile?.name || "").trim().split(/\s+/)[0] || "?";

    const handleOpenBook = (bookId) => {
      const book = books.find((b) => b.id === bookId);
      if (book) { setActiveBook(book); setPage("reader"); }
    };

    const handleOpenHtmlBook = (bookId) => {
      setActiveHtmlBookId(bookId);
      setPage("book-editor");
    };

    const handleDeleteHtmlBook = async (bookId) => {
      try {
        await authedFetch(`${API}/html-books/${bookId}`, { method: "DELETE" });
        setHtmlBooks((prev) => prev.filter((b) => b.id !== bookId));
        showToast("Book deleted.");
      } catch {
        showToast("Delete failed.");
      }
    };

    return (
      <>
        <HomePage
          onNavigate={(p) => setPage(p)}
          onSignOut={() => signOut()}
          billingStatus={billingStatus}
          onGoToBilling={() => setPage("billing")}
          onGoToAdminBilling={() => setPage("admin-billing")}
          onOpenProfile={() => setProfileOpen(true)}
          userInitial={userName}
          books={books}
          htmlBooks={htmlBooks}
          onOpenBook={handleOpenBook}
          onOpenHtmlBook={handleOpenHtmlBook}
          onCreateBook={() => setCreateBookOpen(true)}
          onUploadBook={handleUpload}
          onDeleteHtmlBook={handleDeleteHtmlBook}
          notes={notes}
          subfolders={subfolders}
          folderOrder={folderOrder}
        />
        {profileOpen && (
          <ProfileModal
            profile={profile}
            billingStatus={billingStatus}
            onClose={() => setProfileOpen(false)}
            onUpdate={(updated) => setProfile((prev) => ({ ...prev, ...updated }))}
            onGoToBilling={() => { setProfileOpen(false); setPage("billing"); }}
            onSignOut={() => { setProfileOpen(false); signOut(); }}
          />
        )}
        {createBookOpen && (
          <BookCreatorModal
            onClose={() => setCreateBookOpen(false)}
            onCreated={(newBook) => {
              setHtmlBooks((prev) => [{ id: newBook.id, title: newBook.title }, ...prev]);
              setCreateBookOpen(false);
              setActiveHtmlBookId(newBook.id);
              setPage("book-editor");
            }}
          />
        )}
        {toast && <div className="toast">{toast}</div>}
      </>
    );
  }

  // ── Shared AI panel props ──────────────────────────────────────────
  const defaultTab = "chat";
  const aiPanelProps = {
    book: activeBook,
    api: API,
    showToast,
    notes,
    onAddNote: addNote,
    onDeleteNote: deleteNote,
    onRenameNote: renameNote,
    onUpdateNote: updateNoteContent,
    subfolders,
    onAddSubfolder: addSubfolder,
    onDeleteSubfolder: deleteSubfolder,
    onMoveNote: moveNoteToFolder,
    onMoveFolderToParent: moveFolderToParent,
    folderOrder,
    onReorderFolderOrder: reorderFolderOrder,
  };

  // ── Reader page ────────────────────────────────────────────
  const userName = (profile?.name || "").trim().split(/\s+/)[0] || "?";
  return (
    <>
      <Header
        onSignOut={() => signOut()}
        onHome={() => setPage("home")}
        onOpenProfile={() => setProfileOpen(true)}
        userInitial={userName}
      />
      <div className="main-container">
        {pdfHidden && (
          <div className="pdf-panel-collapsed">
            <button
              className="pdf-toggle-btn"
              onClick={() => setPdfHidden(false)}
              title="Show PDF panel"
            >
              ▶
            </button>
          </div>
        )}
        <div style={{ display: pdfHidden ? "none" : "contents" }}>
          <PdfPanel
            book={activeBook}
            onHide={() => setPdfHidden(true)}
            onUpload={handleUpload}
            onReupload={handleReupload}
            books={books}
            onSelectBook={handleSelectBook}
            onDeleteBook={deleteBook}
            canSaveDocs={profile.permission_to_save_docs === "Y"}
            uploadProgress={uploadProgress}
          />
        </div>
        {!isMobile && (
          <>
            {!pdfHidden && (
              <div className="resize-handle" onMouseDown={handleMouseDown} />
            )}
            {pdfHidden ? (
              <div style={{ flex: 1, display: "flex", minWidth: 0 }}>
                <AiPanel {...aiPanelProps} width="100%" defaultTab={defaultTab} />
              </div>
            ) : (
              <AiPanel {...aiPanelProps} width={panelWidth} defaultTab={defaultTab} />
            )}
          </>
        )}
      </div>

      {/* ── Mobile bottom sheet ───────────────────────── */}
      {isMobile && (
        <>
          {aiSheetOpen && (
            <div className="ai-sheet-backdrop" onClick={() => setAiSheetOpen(false)} />
          )}
          <button
            className="mobile-ai-fab"
            style={{ display: aiSheetOpen ? "none" : "flex" }}
            onClick={() => setAiSheetOpen(true)}
          >
            ✨ AI
          </button>
          <div className={`ai-bottom-sheet${aiSheetOpen ? " open" : ""}`}>
            <div
              className="ai-sheet-drag-handle"
              onClick={() => setAiSheetOpen(false)}
            />
            <AiPanel {...aiPanelProps} width="100%" />
          </div>
        </>
      )}

      {toast && <div className="toast">{toast}</div>}

      {/* ── Profile modal (available from reader too) ─── */}
      {profileOpen && (
        <ProfileModal
          profile={profile}
          billingStatus={billingStatus}
          onClose={() => setProfileOpen(false)}
          onUpdate={(updated) => setProfile((prev) => ({ ...prev, ...updated }))}
          onGoToBilling={() => { setProfileOpen(false); setPage("billing"); }}
          onSignOut={() => { setProfileOpen(false); signOut(); }}
        />
      )}
    </>
  );
}
